import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import './i18n/config';
import { getRankingData } from './utils/dataProcessor';
import { RankingData, SearchResult } from './types';
import { queryClient } from './lib/queryClient';
import ErrorBoundary from './components/ErrorBoundary';
import { useURLSearchParams } from './hooks/useURLSearchParams';

import LanguageSwitch from './components/LanguageSwitch';
import FloatingButtons from './components/FloatingButtons';
import SearchBox from './components/SearchBox';
import RankingTable from './components/RankingTable';
import SearchResultComponent from './components/SearchResult';
import HeroBanner from './components/HeroBanner';
import AboutPanel from './components/AboutPanel';
import Toast from './components/Toast';
import { useToast } from './contexts/ToastContext';

const AppWrapper: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router basename={process.env.PUBLIC_URL}>
            <App />
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const App: React.FC = () => {
  const { t } = useTranslation();
  const { setSearchParam, clearSearchParam } = useURLSearchParams();
  const { showToast } = useToast();
  const [rankingData, setRankingData] = useState<RankingData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [initialSearch, setInitialSearch] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getRankingData();
        setRankingData(data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setInitialSearch(searchParam);
    }
  }, []);

  const handleSearchResult = (result: SearchResult, query: string) => {
    setSearchResult(result);
    if (query) {
      setSearchParam(query);
    }

    if (result.found) {
      showToast('success', t('toast.found'));
      setTimeout(() => {
        const searchResultElement = document.querySelector('[data-search-result="true"]');
        if (searchResultElement) {
          searchResultElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    } else {
      showToast('error', t('toast.notFound'));
    }
  };

  const handleClearSearch = () => {
    setSearchResult(null);
    clearSearchParam();
  };

  return (
    <div className="min-h-screen pb-16">
      <LanguageSwitch />

      <FloatingButtons />

      <HeroBanner />

      <section className="relative mt-10 mb-16 z-60">
        <div className="container mx-auto px-4">
          <SearchBox
            onSearchResult={handleSearchResult}
            initialSearch={initialSearch}
          />

          {searchResult && (
            <div className="mt-10">
              <div className="flex justify-center mb-6">
                <button
                  onClick={handleClearSearch}
                  className="group flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-200"
                >
                  <svg
                    className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span className="text-gray-700 font-semibold group-hover:text-blue-600 transition-colors duration-300">
                    {t('search.back')}
                  </span>
                </button>
              </div>

              <SearchResultComponent result={searchResult} />
            </div>
          )}
        </div>
      </section>

      {!searchResult && (
        <section className="pb-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="section-title inline-block">
                {t('ranktable.title')}
                <div className="w-full h-1 bg-gradient-to-r from-red-200 to-yellow-500 transition-all duration-1000 ease-out mt-2 rounded-full shimmer"></div>
              </h2>
            </div>
            
            <div className="max-w-3xl mx-auto">
              {loading ? (
                <div className="bg-white rounded-xl shadow-xl p-10 text-center">
                  <div className="inline-block w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                  <p className="text-lg text-gray-600">{t('ranktable.loading')}</p>
                </div>
              ) : (
                <RankingTable data={rankingData} />
              )}
            </div>
          </div>
        </section>
      )}

      <footer className="bg-white shadow-md mt-16 pt-10 pb-6 border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <h3 className="text-lg font-bold text-blue-600 flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/logo1.png`} alt="EFPower Logo" className="w-6 h-6 mr-2" /> EFPower
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                © 2025. Powered by <a href="https://x.com/realjaxo" target="_blank" rel="noopener noreferrer" className="text-blue-600">@jaxo</a>          
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6 text-center">
              <a 
                href="https://efp.app/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                  {t('rooter.datafrom')}
              </a>
              <span className="hidden md:inline text-gray-300">|</span>
              <button
								type="button"
								onClick={() => setIsAboutOpen(true)}
								className="text-blue-600 hover:text-blue-800 transition-colors"
							>
								{t('rooter.about')}
							</button>
            </div>
          </div>
        </div>
      </footer>
      <AboutPanel
        isOpen={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
      />
      <Toast />
    </div>
  );
};

export default AppWrapper;
